// =============================================================================
// BP-07-03: ResourceLibraryPage
// Main resource library with filtering and search
// =============================================================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Filter, SlidersHorizontal } from 'lucide-react';
import { useInfiniteResources } from '@/features/resources/hooks/useResources';
import { useFeaturedResources } from '@/features/resources/hooks/useResource';
import { useResourceStore, useFiltersAsParams, useActiveFilterCount } from '@/features/resources/stores/resourceStore';
import {
  ResourceGrid,
  ResourceGridWithLoadMore,
  ResourceSearchBar,
  CategoryNav,
  ResourceFilters,
  FiltersModal,
  ActiveFiltersTags,
  ResourceCard,
} from '../components';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function ResourceLibraryPage() {
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  
  // Get filters from store
  const filters = useFiltersAsParams();
  const activeFilterCount = useActiveFilterCount();
  const { clearFilters } = useResourceStore();

  // Fetch resources with infinite scroll
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteResources(filters);

  // Fetch featured resources
  const { data: featuredResources } = useFeaturedResources(3);

  // Flatten pages into resources array
  const resources = data?.pages.flatMap((page) => page.resources) ?? [];

  return (
    <>
      <Helmet>
        <title>Resource Library | BrightPath</title>
        <meta
          name="description"
          content="Explore our comprehensive library of resources for families navigating birth injuries. Find guides, articles, and tools tailored to your needs."
        />
      </Helmet>

      <div className="min-h-screen bg-cream">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-cream to-cream-dark py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium text-navy mb-4">
                Resource Library
              </h1>
              <p className="text-lg text-warm-gray mb-8">
                Expert guides, practical tools, and trusted information to help your family
                navigate every step of the journey.
              </p>
              
              {/* Search Bar */}
              <div className="max-w-2xl mx-auto">
                <ResourceSearchBar variant="hero" />
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="border-b border-warm-gray/10 bg-white sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <CategoryNav variant="horizontal" showCount={false} />
          </div>
        </section>

        {/* Main Content */}
        <section className="py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex gap-8">
              {/* Sidebar Filters (desktop) */}
              <aside className="hidden lg:block w-64 flex-shrink-0">
                <div className="sticky top-36">
                  <ResourceFilters variant="sidebar" />
                </div>
              </aside>

              {/* Main Content Area */}
              <div className="flex-1 min-w-0">
                {/* Mobile Filter Button & Active Filters */}
                <div className="lg:hidden mb-6">
                  <button
                    type="button"
                    onClick={() => setShowFiltersModal(true)}
                    className={cn(
                      'flex items-center gap-2 px-4 py-2.5 bg-white border border-warm-gray/20 rounded-lg transition-colors',
                      activeFilterCount > 0 && 'border-sage'
                    )}
                  >
                    <SlidersHorizontal className="w-5 h-5 text-warm-gray" />
                    <span className="font-medium text-navy">Filters</span>
                    {activeFilterCount > 0 && (
                      <span className="ml-1 px-2 py-0.5 text-xs font-medium bg-sage text-white rounded-full">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                </div>

                {/* Active Filter Tags */}
                {activeFilterCount > 0 && (
                  <div className="mb-6">
                    <ActiveFiltersTags />
                  </div>
                )}

                {/* Featured Resources (only show if no filters active) */}
                {activeFilterCount === 0 && featuredResources && featuredResources.length > 0 && (
                  <div className="mb-10">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-display text-xl font-semibold text-navy">
                        Featured Resources
                      </h2>
                      <Link
                        to="/resources?featured=true"
                        className="text-sm text-sage hover:text-sage-dark font-medium"
                      >
                        View all
                      </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {featuredResources.map((resource) => (
                        <ResourceCard key={resource.id} resource={resource} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Results Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-display text-xl font-semibold text-navy">
                      {activeFilterCount > 0 ? 'Filtered Results' : 'All Resources'}
                    </h2>
                    {data?.pages[0]?.total !== undefined && (
                      <p className="text-sm text-warm-gray mt-0.5">
                        {data.pages[0].total.toLocaleString()} resources found
                      </p>
                    )}
                  </div>

                  {/* View Mode Toggle (handled in ResourceGrid) */}
                </div>

                {/* Resources Grid */}
                <ResourceGridWithLoadMore
                  resources={resources}
                  isLoading={isLoading}
                  isFetchingNextPage={isFetchingNextPage}
                  hasNextPage={hasNextPage}
                  fetchNextPage={fetchNextPage}
                  emptyMessage={
                    activeFilterCount > 0
                      ? 'No resources match your filters. Try adjusting your criteria.'
                      : 'No resources available yet. Check back soon!'
                  }
                  emptyAction={
                    activeFilterCount > 0
                      ? { label: 'Clear Filters', onClick: clearFilters }
                      : undefined
                  }
                />

                {/* Error State */}
                {error && (
                  <div className="text-center py-12">
                    <p className="text-red-600 mb-4">
                      Unable to load resources. Please try again.
                    </p>
                    <button
                      type="button"
                      onClick={() => window.location.reload()}
                      className="px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
                    >
                      Retry
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Filters Modal (mobile) */}
      <FiltersModal
        isOpen={showFiltersModal}
        onClose={() => setShowFiltersModal(false)}
      />
    </>
  );
}

export default ResourceLibraryPage;


